import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectSpread2 as _objectSpread2 } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { isBoolean, isEmpty } from 'lodash-es';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { StyledTextFieldContainer, StyledInputTitle, StyledInputTitleRequired, StyledTextField } from './InputField.styles.js';
import InputFieldAdornment from './InputFieldAdornment.js';
import StartFieldAdornment from './StartFieldAdornment.js';
import { TYPE } from '../constants/inputConstants.js';
import useHandleError from '../hooks/useHandleError.hooks.js';
const _excluded = ["readonly", "readonlyProps", "title", "onChange", "onEnter", "onEscape", "classes", "prefixWord", "startAdornment", "endAdornment", "disabled", "errors", "placeholder", "inputProps", "InputProps", "type", "control", "rules", "name", "mb", "isRequired", "multiline", "size", "value", "endAdornmentSize", "maxLength", "errorTextAbsolute", "backgroundColor", "noRenderErrorText", "onClean", "rows", "customColor", "borderRadius", "sx", "textAlign", "noBorder"],
  _excluded2 = ["onChange"];
const defaultField = {
  onChange: () => {},
  onBlur: () => {},
  value: null,
  name: ''
};
const InputField = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      readonly,
      readonlyProps,
      title,
      onChange,
      onEnter,
      onEscape,
      classes,
      prefixWord,
      startAdornment,
      endAdornment,
      disabled,
      errors,
      placeholder,
      inputProps,
      InputProps,
      type,
      control,
      rules,
      name,
      mb,
      isRequired,
      multiline,
      size,
      value,
      endAdornmentSize,
      maxLength,
      errorTextAbsolute,
      backgroundColor,
      noRenderErrorText,
      onClean,
      rows,
      customColor,
      borderRadius,
      sx,
      textAlign,
      noBorder
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const hasError = isBoolean(errors) ? errors : !isEmpty(errors);
  const {
    renderErrorText
  } = useHandleError({
    hasError,
    noRenderErrorText,
    errorTextAbsolute
  });
  const handleKeyDown = useCallback(event => {
    const {
      keyCode,
      target
    } = event;
    if (multiline) {
      if (onEnter && keyCode === 13 && !event.shiftKey) {
        // Prevent the default behavior (which would insert a new line)
        event.stopPropagation();
        event.preventDefault();
        onEnter(target.value, event);
      }
      return;
    }
    if (onEnter && keyCode === 13) {
      event.stopPropagation();
      event.preventDefault();
      onEnter(target.value, event);
    }
    if (onEscape && keyCode === 27) {
      event.stopPropagation();
      event.preventDefault();
      onEscape(target.value, event);
    }
  }, [onEnter, onEscape]);
  const handleClean = (onFormChange, handleChange) => {
    onFormChange({
      target: {
        value: ''
      }
    });
    handleChange({
      target: {
        value: ''
      }
    });
    if (onClean) {
      onClean();
    }
  };
  const handleChange = useCallback(event => {
    onChange(event);
  }, [onChange]);
  const isStartAdornmentExist = useCallback(() => !isEmpty(startAdornment) || type === TYPE.SEARCH, [startAdornment, type]);
  const renderInput = _ref2 => {
    let {
        field: {
          onChange: onFormChange
        }
      } = _ref2,
      field = _objectWithoutProperties(_ref2.field, _excluded2);
    return /*#__PURE__*/React__default.createElement(StyledTextFieldContainer, {
      mb: mb
    }, title && /*#__PURE__*/React__default.createElement(StyledInputTitle, {
      error: hasError
    }, title, " ", isRequired && /*#__PURE__*/React__default.createElement(StyledInputTitleRequired, null, "*")), readonly ? /*#__PURE__*/React__default.createElement(Box, readonlyProps, value || '-') : /*#__PURE__*/React__default.createElement(StyledTextField, _extends({}, field, otherProps, {
      size: size,
      sx: sx,
      borderRadius: borderRadius,
      value: value,
      name: name,
      disabled: disabled,
      placeholder: placeholder,
      error: hasError,
      onChange: event => {
        onFormChange(event.target.value);
        handleChange(event);
      },
      textAlign: textAlign,
      onKeyDown: handleKeyDown,
      isStartAdornmentExist: isStartAdornmentExist(),
      fullWidth: true,
      type: type,
      multiline: multiline,
      rows: rows,
      backgroundColor: backgroundColor,
      inputProps: _objectSpread2(_objectSpread2({}, inputProps), {}, {
        maxLength: maxLength || null,
        ref
      }),
      noBorder: noBorder,
      customColor: customColor
      // eslint-disable-next-line react/jsx-no-duplicate-props
      ,

      InputProps: _objectSpread2(_objectSpread2({}, InputProps), {}, {
        startAdornment: /*#__PURE__*/React__default.createElement(StartFieldAdornment, {
          type: type,
          startAdornment: startAdornment,
          prefixWord: prefixWord
        }),
        endAdornment: /*#__PURE__*/React__default.createElement(InputFieldAdornment, {
          type: type,
          value: value,
          size: size,
          maxLength: maxLength,
          handleClean: () => handleClean(onFormChange, handleChange),
          endAdornment: endAdornment || (InputProps === null || InputProps === void 0 ? void 0 : InputProps.endAdornment),
          endAdornmentSize: 20,
          multiline: multiline
        })
      })
    })), hasError && renderErrorText(errors));
  };
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, control ? /*#__PURE__*/React__default.createElement(Controller, {
    name: name,
    control: control,
    rules: rules,
    render: renderInput
  }) : renderInput({
    field: defaultField
  }));
});
InputField.propTypes = {
  value: PropTypes.string,
  /**
   * The inputField title
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Override or extend the styles applied to the component. See CSS API below for more details.
   */
  classes: PropTypes.objectOf(PropTypes.shape({})),
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'error', 'info', 'warning']),
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  /**
   * The size of the component.
   */
  size: PropTypes.oneOf(['extraLarge', 'large', 'medium', 'small', 'extraSmall', 'extraSmallSmall']),
  /**
   * If true,  the component is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * input placeholder
   */
  placeholder: PropTypes.string,
  /**
   * If true, the input is displayed in an error state.
   */
  errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  /**
   * onChangeEvent
   */
  onChange: PropTypes.func,
  /**
   * onEnterEvent
   */
  onEnter: PropTypes.func,
  /**
   * onEscapeEvent
   */
  onEscape: PropTypes.func,
  /**
   * Text placed before the children.
   */
  prefixWord: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Element placed before the children.
   */
  startAdornment: PropTypes.node,
  /**
   * Element placed after the children.
   */
  endAdornment: PropTypes.node,
  /**
   * Attributes applied to the input element.
   */
  inputProps: PropTypes.objectOf(PropTypes.shape({})),
  /**
   * Props applied to the Input element. It will be a FilledInput, OutlinedInput or Input component depending on the variant prop value.
   */
  InputProps: PropTypes.objectOf(PropTypes.shape({})),
  /**
   * Input types 主要是html5的 type 詳情見 mui官方文件
   */
  type: PropTypes.oneOf(['default', 'search', 'password', 'number', 'tel']),
  /**
   * react-hook-form control
   */
  control: PropTypes.shape({}),
  /**
   * input name
   */
  name: PropTypes.string,
  /**
   * react-hook-form validate rules
   */
  rules: PropTypes.shape({}),
  /**
   * is textArea
   */
  multiline: PropTypes.bool,
  /**
   * is readonly
   */
  readonly: PropTypes.bool,
  readonlyProps: PropTypes.shape({}),
  isRequired: PropTypes.bool,
  mb: PropTypes.number,
  endAdornmentSize: PropTypes.number,
  maxLength: PropTypes.number,
  /**
   * 錯誤訊息是否是絕對定位
   */
  errorTextAbsolute: PropTypes.bool,
  backgroundColor: PropTypes.string,
  noRenderErrorText: PropTypes.bool,
  onClean: PropTypes.func,
  rows: PropTypes.number,
  customColor: PropTypes.string,
  borderRadius: PropTypes.number,
  sx: PropTypes.shape({}),
  textAlign: PropTypes.string,
  noBorder: PropTypes.bool
};
InputField.defaultProps = {
  value: '',
  title: null,
  classes: null,
  color: 'secondary',
  variant: 'outlined',
  size: 'extraLarge',
  disabled: false,
  errors: null,
  onChange: () => {},
  onEnter: () => {},
  onEscape: () => {},
  prefixWord: null,
  startAdornment: null,
  endAdornment: null,
  placeholder: '',
  inputProps: {},
  InputProps: {},
  type: 'default',
  control: null,
  rules: {},
  name: '',
  readonly: false,
  readonlyProps: {},
  isRequired: false,
  mb: 0,
  multiline: false,
  endAdornmentSize: 20,
  maxLength: null,
  errorTextAbsolute: false,
  backgroundColor: '',
  noRenderErrorText: false,
  onClean: null,
  rows: null,
  customColor: null,
  borderRadius: 3,
  sx: {},
  textAlign: 'left',
  noBorder: false
};
var InputField$1 = InputField;
export { InputField$1 as default };